import React from 'react';
import PropTypes from 'prop-types';
import { Button, Form } from 'react-bootstrap';
import Icon from '@spark/ui-components/components/Icon';
import { DIAS, MAX_TURNOS, deMinutos, paraMinutos, turnosValidos } from '../utils/janelaDescarga';

const ULTIMO_MINUTO = 23 * 60 + 59;
const TURNO_INICIAL = { horaInicio: '07:00', horaFim: '14:00' };

// Novo turno: 1 h depois do último fechamento, com 2 h de duração, sem passar de 23:59
const proximoTurno = (turnos) => {
  const fins = turnos.map((t) => paraMinutos(t.horaFim)).filter((m) => m !== null);
  if (!fins.length) return { ...TURNO_INICIAL };

  const inicio = Math.min(Math.max(...fins) + 60, ULTIMO_MINUTO);
  return { horaInicio: deMinutos(inicio), horaFim: deMinutos(Math.min(inicio + 120, ULTIMO_MINUTO)) };
};

const copiarTurnos = (turnos) => turnos.map((t) => ({ ...t }));

const DiaTurnos = ({ indice, dia, onChange }) => {
  const nomeDia = DIAS[indice];
  const invalido = dia.aberto && turnosValidos(dia) === null;

  const alterarTurno = (posicao, campo, valor) => onChange({
    ...dia,
    turnos: dia.turnos.map((t, i) => (i === posicao ? { ...t, [campo]: valor } : t))
  });

  const removerTurno = (posicao) => onChange({ ...dia, turnos: dia.turnos.filter((_, i) => i !== posicao) });

  const adicionarTurno = () => onChange({ ...dia, turnos: [...dia.turnos, proximoTurno(dia.turnos)] });

  const alternarDia = () => onChange({
    aberto: !dia.aberto,
    turnos: dia.turnos.length ? dia.turnos : [{ ...TURNO_INICIAL }]
  });

  return (
    <div className="d-flex flex-wrap align-items-start gap-3 py-2 border-bottom">
      <Form.Check
        type="switch"
        id={`janela-dia-${indice}`}
        label={nomeDia}
        checked={dia.aberto}
        onChange={alternarDia}
        style={{ minWidth: 110 }}
      />

      {dia.aberto ? (
        <div className="flex-grow-1">
          <div className="d-flex flex-wrap align-items-center gap-3">
            {dia.turnos.map((turno, posicao) => {
              const n = posicao + 1;
              return (
                // Turno não tem identidade própria: a posição é a chave
                // eslint-disable-next-line react/no-array-index-key
                <div key={posicao} className="d-flex align-items-center gap-1">
                  <Form.Control
                    type="time"
                    size="sm"
                    aria-label={`Abertura do turno ${n} de ${nomeDia}`}
                    value={turno.horaInicio}
                    isInvalid={invalido}
                    onChange={(event) => alterarTurno(posicao, 'horaInicio', event.target.value)}
                  />
                  <span>–</span>
                  <Form.Control
                    type="time"
                    size="sm"
                    aria-label={`Fechamento do turno ${n} de ${nomeDia}`}
                    value={turno.horaFim}
                    isInvalid={invalido}
                    onChange={(event) => alterarTurno(posicao, 'horaFim', event.target.value)}
                  />
                  {dia.turnos.length > 1 ? (
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-danger"
                      aria-label={`Remover turno ${n} de ${nomeDia}`}
                      onClick={() => removerTurno(posicao)}
                    >
                      <Icon name="close" size={14} />
                    </Button>
                  ) : null}
                </div>
              );
            })}
            <Button
              variant="outline-secondary"
              size="sm"
              aria-label={`Adicionar turno em ${nomeDia}`}
              disabled={dia.turnos.length >= MAX_TURNOS}
              onClick={adicionarTurno}
            >
              + Turno
            </Button>
          </div>
          {invalido ? (
            <div className="small text-danger mt-1">
              {`${nomeDia}: o fim do turno deve ser depois do início e os turnos não podem se sobrepor.`}
            </div>
          ) : null}
        </div>
      ) : (
        <span className="text-muted small pt-1">Fechado</span>
      )}
    </div>
  );
};

DiaTurnos.propTypes = {
  indice: PropTypes.number.isRequired,
  dia: PropTypes.shape({
    aberto: PropTypes.bool.isRequired,
    turnos: PropTypes.arrayOf(PropTypes.shape({
      horaInicio: PropTypes.string,
      horaFim: PropTypes.string
    })).isRequired
  }).isRequired,
  onChange: PropTypes.func.isRequired
};

const JanelaTurnosSection = ({ dias, onChange }) => {
  const alterarDia = (indice, novoDia) => onChange(dias.map((dia, i) => (i === indice ? novoDia : dia)));

  // Terça (1) a sexta (4) recebem uma cópia dos turnos de segunda (0)
  const copiarSegunda = () => onChange(dias.map((dia, i) => (
    i >= 1 && i <= 4 ? { aberto: dias[0].aberto, turnos: copiarTurnos(dias[0].turnos) } : dia
  )));

  return (
    <section className="mb-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <h6 className="mb-0">Horário de recebimento</h6>
        <Button variant="outline-primary" size="sm" onClick={copiarSegunda}>
          Copiar segunda para terça–sexta
        </Button>
      </div>
      {dias.map((dia, indice) => (
        <DiaTurnos
          key={DIAS[indice]}
          indice={indice}
          dia={dia}
          onChange={(novoDia) => alterarDia(indice, novoDia)}
        />
      ))}
    </section>
  );
};

JanelaTurnosSection.propTypes = {
  dias: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired
};

export default JanelaTurnosSection;
